/**
 * The eight calculators.
 *
 * Each entry owns its inputs, its computation and its copy. The computation
 * always delegates to lib/calc.js, which mirrors backend/analysis.py and is
 * held to it by tests/test_calculator_parity.py - so a figure here and the same
 * figure in a A$990 briefing cannot disagree.
 *
 * `mirrorsAudit` marks a tool whose arithmetic is the audit's own. The engine
 * function it mirrors is named in a trailing comment - that is documentation
 * for whoever edits this file, never a string the page can render. Internal
 * identifiers do not belong in customer copy.
 */
import {
  marginWaterfall, marketingEfficiency, shippingThreshold,
  discountImpact, priceIncreaseImpact, bundleMargin, unitContribution, pct,
} from "../lib/calc";
import FORMULA_DATA from "../lib/formulas.generated.json";

export const FORMULAS = FORMULA_DATA.formulas;

const CUR = "AUD";
const n = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// Shared input definitions, so two tools asking for the same figure ask for it
// in the same words and units.
const F = {
  revenue: { key: "monthly_gross_revenue", label: "Monthly gross revenue", unit: CUR, placeholder: "312000" },
  orders: { key: "monthly_order_volume", label: "Monthly orders", unit: "orders", placeholder: "2400" },
  returns: { key: "return_rate_pct", label: "Return rate", unit: "%", placeholder: "13" },
  cogs: { key: "cogs_pct_of_aov", label: "COGS as a share of price", unit: "%", placeholder: "44" },
  costPerReturn: { key: "cost_per_return", label: "Cost to process one return", unit: CUR, placeholder: "16" },
  shipping: { key: "onward_shipping_cost", label: "Outbound shipping, per order", unit: CUR, placeholder: "10.40" },
  packaging: { key: "packaging_cost", label: "Packaging, per order", unit: CUR, placeholder: "2.60" },
  gateway: { key: "payment_gateway_fee_pct", label: "Payment gateway fee", unit: "%", placeholder: "1.9" },
  threepl: { key: "fulfillment_3pl_fee", label: "3PL fee, per order", unit: CUR, placeholder: "4.10" },
  marketing: { key: "monthly_marketing_spend", label: "Monthly marketing spend", unit: CUR, placeholder: "71000" },
  overhead: { key: "monthly_fixed_overhead", label: "Monthly fixed overhead", unit: CUR, placeholder: "68000" },
  roas: { key: "blended_roas", label: "Blended ROAS", unit: "x", placeholder: "2.4" },
  // Not a number, and it moves CM1 by 6.6 points on a 13% return rate. Asked
  // rather than assumed, because the honest answer differs by category:
  // apparel resells most returns, anything opened or perishable does not.
  restocked: {
    key: "returns_restocked", label: "Returned products are", type: "choice",
    default: "1",
    options: [
      { value: "1", label: "Put back on the shelf and resold" },
      { value: "0", label: "Discarded or written off" },
    ],
  },
  paidShare: { key: "paid_attributed_revenue_pct", label: "Revenue attributed to paid", unit: "%", placeholder: "62" },
};

const money = (v) => (v === null || v === undefined || !isFinite(v) ? "—"
  : `${CUR} ${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pctS = (v) => (v === null || v === undefined || !isFinite(v) ? "—" : `${v}%`);

const CTA_DEFAULT = {
  line: "This works from figures you already know. The Margin Risk Sweep works from figures your store publishes — and reads a competitor's alongside it.",
  button: "Run a free Margin Risk Sweep",
};

export const TOOLS = [
  // ------------------------------------------------------------------ 1
  {
    slug: "ecommerce-margin-calculator",
    nav: "Ecommerce Margin",
    title: "Ecommerce Margin Calculator",
    tagline: "What the goods leave you, before anything else is paid.",
    intro: "Enter a month of trading and this returns net sales and gross margin — the top of the same waterfall the Margin Audit builds.",
    mirrorsAudit: true,   // analysis.margin_waterfall()
    fields: [F.revenue, F.returns, F.cogs, F.restocked],
    compute(v) {
      const w = marginWaterfall(v);
      const c = w.checkpoints;
      if (!c.net_sales) return null;
      return {
        headline: { label: "CM1 · Gross margin", value: money(c.cm1), sub: pctS(c.cm1_pct) + " of net sales" },
        rows: [
          ["Gross sales", money(c.gross_sales)],
          ["Net sales, after refunds", money(c.net_sales)],
          ["Cost of goods", money(c.net_sales - c.cm1)],
          ["CM1 · Gross margin", money(c.cm1)],
        ],
        gaps: w.gaps,
      };
    },
    formulaKeys: ["Net sales", "COGS", "CM1"],
    limits: "One assumption sits underneath it: COGS is charged on net sales, which treats returned goods as coming back into sellable stock. Where returns are written off instead, the real cost is closer to the gross figure and the margin is lower than this shows.",
    explanation: [
      "Gross margin is not the margin you would work out from a product page. Refunds come out of revenue before the cost of goods is taken off, so a business with a 13% return rate is computing its margin on 87% of what it billed.",
      "This is an upper bound on what the business keeps. Every cost of actually delivering the order — shipping, packaging, payment fees, the 3PL — sits below this line and is what the contribution margin calculator adds.",
    ],
    example: {
      inputs: { monthly_gross_revenue: 312000, return_rate_pct: 13, cogs_pct_of_aov: 44 },
      derived: { "137,280.00": "gross_sales * 0.44", "17,846.40": "gross_sales * 0.44 - (net_sales * 0.44)" },
      title: "A A$312,000 month at 13% returns and 44% COGS",
      body: "Refunds take A$40,560 out before anything else, so net sales are A$271,440. COGS is charged on that — A$119,433.60 — leaving A$152,006.40 of gross margin, 56.0% of net sales. Charge it on the gross figure instead, as you would if returned stock could not be resold, and the cost is A$137,280: A$17,846.40 higher, and the margin the same amount lower.",
    },
  },

  // ------------------------------------------------------------------ 2
  {
    slug: "contribution-margin-calculator",
    nav: "Contribution Margin",
    title: "Contribution Margin Calculator",
    tagline: "CM1, CM2 and CM3 — the whole waterfall, line by line.",
    intro: "This is the exact calculation the Margin Audit runs on a client's own P&L. Leave a field blank and the line is skipped and declared, never filled with a zero.",
    mirrorsAudit: true,   // analysis.margin_waterfall()
    fields: [F.revenue, F.orders, F.returns, F.cogs, F.restocked, F.costPerReturn,
             F.shipping, F.packaging, F.gateway, F.threepl, F.marketing, F.overhead],
    compute(v) {
      const w = marginWaterfall(v);
      if (!w.rows.length) return null;
      const c = w.checkpoints;
      return {
        headline: {
          label: c.net_is_final ? "Net profit" : "Contribution before fixed overhead",
          value: money(c.net), sub: pctS(c.net_pct) + " of net sales",
        },
        rows: w.rows.map((r) => [r.label, money(r.value)]),
        marks: [
          ["CM1", money(c.cm1), pctS(c.cm1_pct)],
          ["CM2", money(c.cm2), pctS(c.cm2_pct)],
          ["CM3", money(c.cm3), pctS(c.cm3_pct)],
        ],
        gaps: w.gaps,
      };
    },
    formulaKeys: ["Net sales", "CM1", "CM2", "CM3", "Net profit"],
    explanation: [
      "Contribution margin is stacked, not single. CM1 is what the goods leave. CM2 is what survives getting the order to the customer and handling the ones that come back. CM3 is what survives acquiring the customer in the first place — and CM3 is the line that has to cover every fixed cost in the business.",
      "CM2 is where free shipping and a heavy returns rate show up, because neither touches the number on the product page. Discounting lands higher, in net sales and CM1 — but the effect is the same shape: a range can hold its list margin while what it actually keeps falls.",
      "A blank field is not a zero here. A cost you did not enter is recorded as missing and named below the result, because filling it with zero would overstate what you keep — which is exactly the flattery this calculator exists to avoid.",
    ],
    example: {
      inputs: { monthly_gross_revenue: 312000, monthly_order_volume: 2400, return_rate_pct: 13,
                cogs_pct_of_aov: 44, cost_per_return: 16, onward_shipping_cost: 10.4,
                packaging_cost: 2.6, payment_gateway_fee_pct: 1.9, fulfillment_3pl_fee: 4.1,
                monthly_marketing_spend: 71000, monthly_fixed_overhead: 68000 },
      derived: { "19": "cm1_pct - cm2_pct" },
      title: "Where a 56% gross margin ends up",
      body: "On A$312,000 of gross revenue across 2,400 orders, CM1 of A$152,006.40 (56.0% of net sales) becomes CM2 of A$100,046.40 (36.9%) once return handling, shipping, packaging, gateway fees and the 3PL are paid. That is 19 points of margin, none of it visible on a product page. Marketing of A$71,000 takes CM3 to A$29,046.40 (10.7%), and A$68,000 of fixed overhead makes the month a A$38,953.60 loss.",
    },
  },

  // ------------------------------------------------------------------ 3
  {
    slug: "breakeven-roas-calculator",
    nav: "Break-even ROAS",
    title: "Ecommerce Break-even ROAS Calculator",
    tagline: "The ROAS at which a marketing dollar returns exactly one dollar.",
    intro: "Computed from your own contribution rate, not from a category average — because no published benchmark describes marketing spend for sub-A$10M Australian DTC.",
    mirrorsAudit: true,   // analysis.marketing_efficiency()
    fields: [F.revenue, F.orders, F.returns, F.cogs, F.restocked, F.costPerReturn,
             F.shipping, F.packaging, F.gateway, F.threepl, F.roas],
    compute(v) {
      const w = marginWaterfall(v);
      const e = marketingEfficiency(v, w);
      if (!e.breakeven_roas) return null;
      return {
        headline: {
          label: "Break-even ROAS",
          value: `${e.breakeven_roas}x`,
          sub: e.marketing_is_accretive
            ? `You reported ${e.roas}x — above break-even`
            : `You reported ${e.roas}x — below break-even`,
        },
        rows: [
          ["CM2 rate", pctS(e.contribution_rate_pct)],
          ["Net of returns", pctS(e.net_of_returns_pct)],
          ["Break-even ROAS", `${e.breakeven_roas}x`],
          ["Your blended ROAS", `${e.roas}x`],
          ["Contribution per marketing spend", money(e.contribution_per_marketing_dollar)],
          ["Value created per dollar spent", `${e.value_per_dollar_cents} cents`],
        ],
        verdict: e.marketing_is_accretive
          ? "At this ROAS the next marketing dollar creates contribution."
          : "At this ROAS the next marketing dollar destroys value. Spending more makes the month worse, not better.",
        gaps: w.gaps,
      };
    },
    formulaKeys: ["Breakeven ROAS", "Contribution per marketing spend", "ROAS", "CM2"],
    explanation: [
      "A break-even ROAS taken from an industry article is worthless, because it is a claim about someone else's cost structure. The number that matters is derived from yours: a marketing dollar has to be repaid out of contribution, so the threshold is set by your CM2 rate and your return rate and nothing else.",
      "Below the break-even figure the paid programme returns less contribution than it costs. That is arithmetic on your own numbers, not an opinion about your creative — with one honest caveat: a blended ROAS describes the average dollar already spent, and the next dollar usually performs worse than the average, not better.",
      "The one assumption: blended ROAS as you report it. If your attribution overstates paid revenue, the break-even point is unchanged but your reported ROAS is not — and the gap between them is larger than it looks.",
    ],
    example: {
      inputs: { monthly_gross_revenue: 312000, monthly_order_volume: 2400, return_rate_pct: 13,
                cogs_pct_of_aov: 44, cost_per_return: 16, onward_shipping_cost: 10.4,
                packaging_cost: 2.6, payment_gateway_fee_pct: 1.9, fulfillment_3pl_fee: 4.1,
                blended_roas: 2.4 },
      derived: { "77": "contribution_per_marketing_dollar * 100" },
      title: "Why 2.4x can still be losing money",
      body: "A CM2 rate of 36.9% and net sales at 87.0% of gross put break-even ROAS at 3.12x. A blended 2.4x therefore returns about 77 cents of contribution for every dollar spent — the marketing is working in the sense that it produces revenue, and destroying value in the sense that matters.",
    },
  },

  // ------------------------------------------------------------------ 4
  {
    slug: "free-shipping-threshold-calculator",
    nav: "Free Shipping Threshold",
    title: "Free Shipping Threshold Calculator",
    tagline: "Where the threshold has to sit before it stops costing you money.",
    intro: "Needs two figures and refuses without them: your real per-order shipping cost, and your real contribution rate.",
    mirrorsAudit: true,   // analysis.shipping_threshold()
    fields: [
      { key: "ship_cost", label: "Outbound shipping, per order", unit: CUR, placeholder: "10.40" },
      { key: "contribution_rate_pct", label: "Contribution margin rate (CM2)", unit: "%", placeholder: "36.1" },
      { key: "median_price", label: "Your median product price", unit: CUR, placeholder: "118.00", optional: true },
    ],
    compute(v) {
      const r = shippingThreshold(n(v.ship_cost), n(v.contribution_rate_pct), n(v.median_price));
      if (r.breakeven_threshold === "data_unavailable") {
        // Deliberately NOT r.why_unavailable. That string is written for the
        // audit, where the figures come from an intake form and a catalogue -
        // neither of which exists on this page. calc.js still mirrors the
        // Python exactly, so parity is untouched; only the wording differs,
        // because the audience does.
        return {
          unavailable:
            "This needs both figures to mean anything: what one order actually costs you to ship, and what share of a sale you keep after every variable cost. A threshold worked out against a guessed margin is a made-up number with a decimal point on it.",
        };
      }
      const rows = [
        ["Shipping cost per order", money(r.ship_cost)],
        ["Contribution rate", pctS(r.contribution_rate_pct)],
        ["Break-even threshold", money(r.breakeven_threshold)],
      ];
      if (r.items_needed_at_median) {
        rows.push(["Median-priced items needed to reach it", `${r.items_needed_at_median}`]);
      }
      return {
        headline: { label: "Break-even free-shipping threshold", value: money(r.breakeven_threshold),
                    sub: "below this, every order that just clears it loses money on freight" },
        rows,
        // A median says where the middle of the range sits and nothing about
        // its tails. It cannot tell you what SHARE of your products clear the
        // threshold - that needs the price list, which this page does not have.
        verdict: r.items_needed_at_median
          ? (r.items_needed_at_median > 1.6
              ? `A customer buying at your median price needs about ${r.items_needed_at_median} items to reach it, so for a typical basket this is a multi-item minimum rather than a free-shipping offer.`
              : `A single median-priced item clears it, so for a typical basket the threshold costs you nothing on freight. Your headroom is the gap between it and whatever you currently advertise.`)
          : null,
      };
    },
    formulaKeys: ["CM2"],
    explanation: [
      "A free-shipping threshold is a margin decision dressed as a marketing one. The order has to generate enough contribution to pay the freight you just absorbed, so the threshold is your shipping cost divided by your contribution rate — not a round number that looked friendly.",
      "Set it below break-even and the arithmetic runs the wrong way: the more orders clear the threshold, the more the policy costs. Volume makes it worse.",
      "What it cannot tell you is how much of your range sits below the threshold. A median describes the middle of a price list and says nothing about its tails, so the cheap end — the accessory, the refill, the sample size — is invisible to a single summary figure.",
    ],
    example: {
      inputs: { ship_cost: 10.4, contribution_rate_pct: 36.9 },
      derived: { "50": "50", "25": "25", "21.82": "50 - ship_cost / (contribution_rate_pct / 100)" },
      title: "A A$10.40 freight cost at a 36.9% contribution rate",
      body: "Break-even sits at A$28.18. Any threshold above that costs nothing on freight, even on an order that only just qualifies. A A$50 threshold therefore carries A$21.82 of cushion, and the question stops being whether it loses money. Set it at A$25 instead and the arithmetic inverts: every order that only just qualifies is underwater, and the more of them you get the more the policy costs.",
    },
  },

  // ------------------------------------------------------------------ 5
  {
    slug: "discount-margin-calculator",
    nav: "Discount Margin",
    title: "Discount Margin Calculator",
    tagline: "What a discount costs, and the volume it has to buy back.",
    intro: "A discount comes entirely out of margin, never out of cost. This shows how much extra you must sell just to stand still.",
    mirrorsAudit: false,
    fields: [
      { key: "price", label: "Selling price", unit: CUR, placeholder: "89.00" },
      { key: "unitCost", label: "Cost per unit, all variable costs", unit: CUR, placeholder: "43.00" },
      { key: "discountPct", label: "Discount", unit: "%", placeholder: "20" },
    ],
    compute(v) {
      const r = discountImpact({ price: n(v.price), unitCost: n(v.unitCost), discountPct: n(v.discountPct) });
      if (!r) return null;
      return {
        headline: {
          label: r.destroys_contribution ? "This discount sells below cost" : "Extra volume needed to stand still",
          value: r.destroys_contribution ? money(r.margin_after) : `+${r.volume_needed_pct}%`,
          sub: r.destroys_contribution ? "every unit sold makes the month worse"
            : `margin falls from ${money(r.margin_before)} to ${money(r.margin_after)} per unit`,
        },
        rows: [
          ["Price before", money(r.price)],
          ["Price after discount", money(r.new_price)],
          ["Margin before", `${money(r.margin_before)} (${pctS(r.margin_before_pct)})`],
          ["Margin after", `${money(r.margin_after)} (${pctS(r.margin_after_pct)})`],
          ["Share of margin given away", pctS(r.margin_lost_pct)],
          ["Units must rise by", r.volume_needed_pct === null ? "—" : `${r.volume_needed_pct}%`],
        ],
      };
    },
    formulaKeys: [],
    explanation: [
      "The trap in discounting is that the percentage off the price is not the percentage off the margin. Costs do not move, so the whole discount is taken out of contribution — and if your margin is 50%, a 20% discount removes 40% of it.",
      "That is why the volume figure matters more than the discount figure. A promotion that needs sales to rise 67% to earn what the full-price line earned is not a promotion, it is a decision to sell more for less.",
      "Enter every variable cost in the unit cost, not just the cost of goods. Freight, packaging and payment fees are all paid on a discounted order too.",
    ],
    example: {
      inputs: { price: 89, unitCost: 43, discountPct: 20 },
      title: "20% off a A$89 product costing A$43",
      body: "The price falls to A$71.20 and the margin from A$46.00 to A$28.20 — the 20% discount removed 38.7% of the contribution. Units must rise 63.1% for the discounted line to earn what it did at full price. If the promotion lifts volume by half that, it lost money while looking busy.",
    },
  },

  // ------------------------------------------------------------------ 6
  {
    slug: "bundle-margin-calculator",
    nav: "Bundle Margin",
    title: "Bundle Margin Calculator",
    tagline: "What a bundle earns against the singles it is made of.",
    intro: "Add the components, set the bundle price, and see the margin you are handing over for the larger basket.",
    mirrorsAudit: false,
    fields: [
      { key: "c1p", label: "Component 1 — price", unit: CUR, placeholder: "39.00" },
      { key: "c1c", label: "Component 1 — cost", unit: CUR, placeholder: "17.00" },
      { key: "c2p", label: "Component 2 — price", unit: CUR, placeholder: "29.00" },
      { key: "c2c", label: "Component 2 — cost", unit: CUR, placeholder: "12.00" },
      { key: "c3p", label: "Component 3 — price", unit: CUR, placeholder: "25.00", optional: true },
      { key: "c3c", label: "Component 3 — cost", unit: CUR, placeholder: "11.00", optional: true },
      { key: "bundlePrice", label: "Bundle price", unit: CUR, placeholder: "79.00" },
    ],
    compute(v) {
      const components = [
        { price: n(v.c1p), cost: n(v.c1c) },
        { price: n(v.c2p), cost: n(v.c2c) },
        { price: n(v.c3p), cost: n(v.c3c) },
      ];
      const r = bundleMargin({ components, bundlePrice: n(v.bundlePrice) });
      if (!r) return null;
      return {
        headline: {
          label: r.below_cost ? "This bundle sells below its cost of goods" : "Bundle contribution",
          value: money(r.bundle_margin),
          sub: `${pctS(r.bundle_margin_pct)} of the bundle price, against ${pctS(r.singles_margin_pct)} on the singles`,
        },
        rows: [
          [`Sum of ${r.components} singles`, money(r.sum_of_singles)],
          ["Bundle price", money(r.bundle_price)],
          ["Discount against singles", `${money(r.discount_vs_singles)} (${pctS(r.discount_vs_singles_pct)})`],
          ["Cost of goods in the bundle", money(r.cost_of_goods)],
          ["Margin if sold separately", `${money(r.singles_margin)} (${pctS(r.singles_margin_pct)})`],
          ["Margin as a bundle", `${money(r.bundle_margin)} (${pctS(r.bundle_margin_pct)})`],
          ["Margin handed over", money(r.margin_given_away)],
        ],
        verdict: r.below_cost
          ? "The bundle price does not cover the goods inside it, before any freight or fees."
          : null,
      };
    },
    formulaKeys: [],
    explanation: [
      "A bundle is a discount with better manners. The cost of goods is the sum of the components whatever you charge, so every dollar off the combined price comes out of contribution exactly as a markdown would.",
      "The question a bundle has to answer is whether the larger basket is worth the margin handed over — and that depends on whether the customer would have bought the items anyway. A bundle of things people already buy together is a discount on an order you had.",
      "Margin Lens treats bundles as their own category and never prices them against singles when comparing two stores, for the same reason: a gift box measured against one product is not a price difference.",
    ],
    example: {
      inputs: { c1p: 39, c1c: 17, c2p: 29, c2c: 12, c3p: 25, c3c: 11, bundlePrice: 79 },
      derived: { "26.4": "((c1p + c2p + c3p) - bundlePrice) / ((c1p + c2p + c3p) - (c1c + c2c + c3c)) * 100" },
      title: "Three items worth A$93 sold as a A$79 bundle",
      body: "Cost of goods is A$40 whichever way it sells. Separately the three earn A$53 of contribution; as a bundle they earn A$39 — a 15.1% discount off the price has taken 26.4% of the margin. Worth it if it moves a slow third item, expensive if all three were selling anyway.",
    },
  },

  // ------------------------------------------------------------------ 7
  {
    slug: "price-increase-calculator",
    nav: "Price Increase",
    title: "Price Increase Calculator",
    tagline: "How much volume you can afford to lose.",
    intro: "A price rise goes straight to contribution, so it buys room to lose volume. This says how much room.",
    mirrorsAudit: false,
    fields: [
      { key: "price", label: "Current price", unit: CUR, placeholder: "89.00" },
      { key: "unitCost", label: "Cost per unit, all variable costs", unit: CUR, placeholder: "43.00" },
      { key: "increasePct", label: "Price increase", unit: "%", placeholder: "10" },
    ],
    compute(v) {
      const r = priceIncreaseImpact({ price: n(v.price), unitCost: n(v.unitCost), increasePct: n(v.increasePct) });
      if (!r) return null;
      return {
        headline: {
          label: "Volume you can afford to lose",
          value: r.volume_can_lose_pct === null ? "—" : `${r.volume_can_lose_pct}%`,
          sub: `margin rises from ${money(r.margin_before)} to ${money(r.margin_after)} per unit`,
        },
        rows: [
          ["Price before", money(r.price)],
          ["Price after", money(r.new_price)],
          ["Margin before", `${money(r.margin_before)} (${pctS(r.margin_before_pct)})`],
          ["Margin after", `${money(r.margin_after)} (${pctS(r.margin_after_pct)})`],
          ["Margin gain per unit", pctS(r.margin_gain_pct)],
          ["Units may fall by", r.volume_can_lose_pct === null ? "—" : `${r.volume_can_lose_pct}%`],
        ],
      };
    },
    formulaKeys: [],
    explanation: [
      "A price increase brings no cost of its own. There is no extra unit to make, ship or pay a platform for, so the whole rise lands in contribution — which is what separates it from selling more.",
      "The number that decides it is not how much margin you gain, but how much volume that gain lets you lose. A rise with 18% of headroom holds while unit volume falls by less than that 18% — and whether it will is a judgement about your customers, not about your spreadsheet.",
      "This calculator says nothing about whether demand will hold. Nothing computed from cost data can. It sizes the bet; you take it.",
    ],
    example: {
      inputs: { price: 89, unitCost: 43, increasePct: 10 },
      title: "10% on a A$89 product costing A$43",
      body: "The price moves to A$97.90 and the unit margin from A$46.00 to A$54.90, a 19.3% gain. You could then sell 16.2% fewer units and be exactly where you started. Whether demand falls by more or less than that is the judgement; the 16.2% is the arithmetic.",
    },
  },

  // ------------------------------------------------------------------ 8
  {
    slug: "sku-profitability-calculator",
    nav: "SKU Profitability",
    title: "SKU Profitability Calculator",
    tagline: "What one unit actually leaves, after everything.",
    intro: "The same contribution logic as the waterfall, applied to a single product instead of a month.",
    mirrorsAudit: false,
    fields: [
      { key: "price", label: "Selling price", unit: CUR, placeholder: "89.00" },
      { key: "unitCost", label: "Cost of goods", unit: CUR, placeholder: "34.00" },
      { key: "shipping", label: "Outbound shipping", unit: CUR, placeholder: "10.40" },
      { key: "packaging", label: "Packaging", unit: CUR, placeholder: "2.60" },
      { key: "gatewayPct", label: "Payment gateway fee", unit: "%", placeholder: "1.9" },
      { key: "returnRatePct", label: "Return rate for this SKU", unit: "%", placeholder: "13" },
      { key: "costPerReturn", label: "Cost to process one return", unit: CUR, placeholder: "16" },
      { key: "adCostPerUnit", label: "Acquisition cost per unit", unit: CUR, placeholder: "18.00", optional: true },
    ],
    compute(v) {
      const r = unitContribution({
        price: n(v.price), unitCost: n(v.unitCost), shipping: n(v.shipping),
        packaging: n(v.packaging), gatewayPct: n(v.gatewayPct),
        returnRatePct: n(v.returnRatePct), costPerReturn: n(v.costPerReturn),
        adCostPerUnit: n(v.adCostPerUnit),
      });
      if (!r) return null;
      return {
        headline: {
          label: r.contribution > 0 ? "Contribution per unit" : "This SKU loses money on every sale",
          value: money(r.contribution), sub: `${pctS(r.contribution_pct)} of the selling price`,
        },
        rows: [["Selling price", money(r.price)], ...r.rows.map((x) => [x.label, money(x.value)]),
               ["Contribution", money(r.contribution)]],
        verdict: r.contribution <= 0
          ? "Every unit sold makes the month worse. Volume is not the fix."
          : null,
      };
    },
    formulaKeys: ["CM2"],
    explanation: [
      "A SKU-level margin taken as price minus cost of goods is the number that hides unprofitable products. The costs that vary by unit — freight, packaging, the gateway's slice, the share of them that comes back — are exactly the ones that fall hardest on cheap, heavy or returned-often items.",
      "Include acquisition only if the SKU is genuinely bought through paid traffic. Loading blended ad cost onto every unit will condemn products that sell themselves.",
      "This applies the audit\u2019s contribution logic to one unit rather than to a month. If the answer here is negative, no amount of volume repairs it.",
    ],
    example: {
      inputs: { price: 89, unitCost: 34, shipping: 10.4, packaging: 2.6, gatewayPct: 1.9,
                returnRatePct: 13, costPerReturn: 16, adCostPerUnit: 18 },
      derived: { "55": "price - 34", "61.8": "(price - 34) / price * 100",
                 "38.23": "price - 34 - 10.4 - 2.6 - 1.69 - 2.08",
                 "43.0": "(price - 34 - 10.4 - 2.6 - 1.69 - 2.08) / price * 100" },
      title: "A A$89 product that looks like 62% margin",
      body: "Price A$89 less A$34 of goods reads as A$55, or 61.8%. Take off A$10.40 of freight, A$2.60 of packaging, A$1.69 of gateway fees and A$2.08 of returns provision and the contribution is A$38.23, 43.0%. Add A$18 of acquisition and it is A$20.23 — 22.7%, on a product a gross-margin report would have called a strong performer.",
    },
  },
];

export const TOOL_BY_SLUG = Object.fromEntries(TOOLS.map((t) => [t.slug, t]));
export { CTA_DEFAULT, pct };
